import { Wrench, ChevronDown } from "lucide-react";
import { MenuButton } from "~/components/assistant/MenuButton";
import { PopoverButton } from "~/components/assistant/PopoverButton";
import type { MCPServer } from "~/components/assistant/types";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";

interface ToolSelectorProps {
  servers: MCPServer[];
  selectedServerIds: string[];
  onToggle: (serverId: string) => void;
}

export function ToolSelector({ servers, selectedServerIds, onToggle }: ToolSelectorProps) {
  const isDisabled = servers.length === 0;
  const summaryLabel =
    selectedServerIds.length > 0
      ? `${selectedServerIds.length} Tool${selectedServerIds.length !== 1 ? "s" : ""}`
      : "No Tools";

  return (
    <Tooltip delayDuration={700}>
      <Popover>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <PopoverButton
              variant="ghost"
              disabled={isDisabled}
              className="disabled:opacity-50 disabled:cursor-not-allowed has-[>svg]:px-2"
              aria-label="Select Tools"
            >
              <Wrench className="size-4 text-gray-600" />
              <div className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-gray-700 text-[13px] text-left text-nowrap">
                <p className="block leading-[16px] whitespace-pre">{summaryLabel}</p>
              </div>
              <ChevronDown className="size-4 text-gray-400" />
            </PopoverButton>
          </PopoverTrigger>
        </TooltipTrigger>
        <PopoverContent className="w-84 p-2 max-h-[450px] overflow-y-auto" align="start">
          <div>
            {servers.map((server) => (
              <MenuButton
                key={server.id}
                onClick={() => onToggle(server.id)}
                label={
                  <div className="flex flex-col pl-1">
                    <span className="text-[13px] leading-[16px] font-medium">{server.name}</span>
                    {server.description && (
                      <span className="text-[12px] leading-[16px] text-gray-600 mt-1">
                        {server.description}
                      </span>
                    )}
                  </div>
                }
                ariaLabel="Select MCP servers"
                iconAlign="top"
                leftIcon={<Wrench className="size-4 text-gray-600" />}
                checkmark="right"
                isSelected={selectedServerIds.includes(server.id)}
                disabled={server.default}
              />
            ))}
          </div>
        </PopoverContent>
      </Popover>
      <TooltipContent>
        <p>Select Data Tools</p>
      </TooltipContent>
    </Tooltip>
  );
}
